import { useEffect, useState } from 'react';
import api from '@/lib/api';

// Shared, read-only category search index (spellings, linked account, synonyms).
let cache = null;
let inflight = null;

const norm = s => (s || '').toLowerCase().replace(/[\s\-_&/,]+/g, ' ').trim();

function load() {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = api.get('/accounting/category-search-index')
      .then(r => { cache = r.data || { categories: [], synonyms: {} }; return cache; })
      .catch(() => ({ categories: [], synonyms: {} }))
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/** Returns match(categoryName, query) → null | { via: 'name'|'spelling'|'account'|'code'|'synonym', text } */
export function useCategorySearch() {
  const [idx, setIdx] = useState(cache);
  useEffect(() => { if (!idx) load().then(setIdx); }, [idx]);

  // Category row from the index; unused built-in categories resolve through their default synonyms.
  const accountOf = name => {
    const k = norm(name);
    const hit = idx?.categories?.find(x => x.key === k);
    if (hit || !idx) return hit || null;
    const acc = Object.keys(idx.synonyms || {}).find(a => idx.synonyms[a].some(s => norm(s) === k));
    return acc ? { key: k, variants: [name], account_name: acc, account_code: idx.codes?.[acc] || null } : null;
  };

  const match = (name, query) => {
    const q = norm(query);
    if (!q) return { via: 'all' };
    if (norm(name).includes(q)) return { via: 'name' };
    const c = accountOf(name);
    if (!c) return null;
    const v = c.variants.find(x => norm(x).includes(q));
    if (v) return { via: 'spelling', text: v };
    if (c.account_code && c.account_code.toLowerCase().includes((query || '').trim().toLowerCase())) return { via: 'code', text: `${c.account_code} ${c.account_name || ''}` };
    if (c.account_name && norm(c.account_name).includes(q)) return { via: 'account', text: c.account_name };
    const syn = (idx.synonyms?.[c.account_name] || []).find(s => norm(s).includes(q));
    if (syn) return { via: 'synonym', text: syn };
    return null;
  };

  return { match, accountOf, ready: !!idx };
}
